import React, { useEffect, useState } from "react";
import { DatePicker } from "antd";
import { InputNumber } from "antd";
import dayjs from "dayjs";
import { fetchTemperatureData, getTodayDate } from "./utils";
import { BACKEND_DATE_FORMAT, UI_DATE_FORMAT } from "../../../consts";
const { RangePicker } = DatePicker;
const Forecast = ({
  currentSite,
  siteMetadata,
  currentCrop,
  currentGreenhouse,
  currentPlot,
  isImperial = false,
  averageData,
}) => {
  const DATE_FORMAT = "YYYY-MM-DD";
  const GRAPH_UI_DATE_FORMAT = "DD MMM YY";

  const [averageDifferenceForRange, setAverageDifferenceForRange] =
    useState(null);
  const [forecastDays, setForecastDays] = useState(4);
  const [gdd, setGdd] = useState(0);
  const [selectedRangeGdd, setSelectedRangeGdd] = useState(null);
  const [forecastGdd, setForecastGdd] = useState(0);
  const [plantingDate, setPlantingDate] = useState(null);
  const [harvestDate, setHarvestDate] = useState(null);
  const [selectedGDDDates, setSelectedGDDDates] = useState([]);
  const [latLong, setLatLong] = useState(null);
  const [gddBaseTemperature, setGddBaseTemperature] = useState(0);

  useEffect(() => {
    if (currentSite) {
      const lat = currentSite.lat;
      const long = currentSite.long;
      setLatLong(`${lat},${long}`);
    }
  }, [currentSite]);

  useEffect(() => {
    if (siteMetadata && currentCrop && currentGreenhouse && currentPlot) {
      const tableData =
        siteMetadata?.[currentGreenhouse.name]?.[currentPlot.name]?.[
          "table_data"
        ];
      const gddBaseTemperature =
        siteMetadata?.[currentGreenhouse.name]?.[currentPlot.name]?.[
          "GDD_BASE_TEMPERATURE_IN_CELSIUS"
        ];
      if (tableData) {
        setPlantingDate(tableData?.[0]?.plantingDate || null);
      }
      if (tableData) {
        setHarvestDate(tableData?.[0]?.harvestDate || null);
      }
      if (gddBaseTemperature) {
        setGddBaseTemperature(gddBaseTemperature);
      }
    }
  }, [siteMetadata, currentCrop, currentGreenhouse, currentPlot]);

  useEffect(() => {
    const averageDifference = calculateDifferenceForDateRange(selectedGDDDates);
    setAverageDifferenceForRange(averageDifference);
  }, [selectedGDDDates, averageData]);

  useEffect(() => {
    const calculateInitialGdd = async () => {
      if (plantingDate) {
        const today = getTodayDate(latLong);

        const endDate = harvestDate
          ? dayjs(harvestDate).isBefore(today)
            ? dayjs(harvestDate)
            : today
          : today;
        const defaultRange = [dayjs(plantingDate), endDate];
        setSelectedGDDDates(defaultRange);

        // const tempDataTillToday = await fetchTemperatureData(
        //   latLong,
        //   plantingDate,
        //   today,
        // );
        const tempData = await fetchTemperatureData(
          latLong,
          plantingDate,
          endDate.format(DATE_FORMAT),
          siteMetadata,
        );
        // setGdd(calculateGDD(tempDataTillToday));
        setSelectedRangeGdd(calculateGDD(tempData));
      }
    };
    calculateInitialGdd();
  }, [plantingDate]);

  const calculateGDD = (
    temperatureData,
    baseTemperature = gddBaseTemperature,
  ) => {
    let totalGDD = 0;
    if (temperatureData) {
      temperatureData.forEach(({ max, min }) => {
        const dailyGDD = (max + min) / 2 - baseTemperature;
        totalGDD += dailyGDD > 0 ? dailyGDD : 0;
      });
    }
    return totalGDD;
  };

  const calculateDifferenceForDateRange = (selectedDates) => {
    if (averageData.length === 0 || selectedDates.length !== 2) return null;

    const [startDate, endDate] = selectedDates;

    // Filter average data for the selected date range using isBetween
    const filteredAverages = averageData.filter(({ date }) =>
      dayjs(date, GRAPH_UI_DATE_FORMAT).isBetween(
        startDate,
        endDate,
        null,
        "[]",
      ),
    );

    if (filteredAverages.length < 2) return null; // No data in the selected range

    // Sort the filtered averages based on the date to get the leftmost and rightmost
    const sortedAverages = filteredAverages.sort((a, b) =>
      dayjs(a.date, GRAPH_UI_DATE_FORMAT).diff(
        dayjs(b.date, GRAPH_UI_DATE_FORMAT),
      ),
    );

    const leftmostAverage = sortedAverages[0].averageLength; // Leftmost date's average
    const rightmostAverage =
      sortedAverages[sortedAverages.length - 1].averageLength; // Rightmost date's average

    // Calculate the difference between leftmost and rightmost average
    return rightmostAverage - leftmostAverage;
  };

  const handleGDDRangeChange = async (dates) => {
    if (dates) {
      const [start, end] = dates.map((date) => date.format(DATE_FORMAT));
      const tempData = await fetchTemperatureData(
        latLong,
        start,
        end,
        siteMetadata,
      );
      const gddResult = calculateGDD(tempData);
      setSelectedRangeGdd(gddResult);
      setSelectedGDDDates(dates);

      // Calculate the sum of averages for the selected date range
      const totalAverage = calculateDifferenceForDateRange(dates);
      setAverageDifferenceForRange(totalAverage);
    }
  };

  const handleGDDForecastChange = async (val) => {
    setForecastDays(val);
    const tomorrow = getTodayDate(latLong)
      .add(1, "day")
      .format(BACKEND_DATE_FORMAT);
    const forecastEnd = getTodayDate(latLong)
      .add(val, "day")
      .format(BACKEND_DATE_FORMAT);
    const tempData = await fetchTemperatureData(
      latLong,
      tomorrow,
      forecastEnd,
      siteMetadata,
    );
    const total = calculateGDD(tempData);
    setForecastGdd(total);
  };

  const disabledDate = (current) => {
    return (
      current &&
      (current < dayjs(plantingDate) || current > dayjs().endOf("day"))
    );
  };

  useEffect(() => {
    // Automatically calculate GDD for forecast days on load
    handleGDDForecastChange(forecastDays);
  }, [forecastDays, latLong]);

  return (
    <div className="forecast-card w-full ">
      <h2 className="font-bold text-lg mb-3">GDD and Broccoli Growth Trend</h2>
      {/*<h3 className="mb-2">*/}
      {/*  GDD from Planting Date ({dayjs(plantingDate).format(GRAPH_UI_DATE_FORMAT)}) to*/}
      {/*  Today: {"  "}*/}
      {/*  <span className="font-bold">{gdd.toFixed(2)}</span>*/}
      {/*</h3>*/}
      <div className="flex items-center sm:items-start justify-between mb-3 sm:flex-col">
        <div>
          <span>GDD from &nbsp;</span>
          <RangePicker
            value={selectedGDDDates}
            onChange={handleGDDRangeChange}
            disabledDate={disabledDate}
            format={UI_DATE_FORMAT}
          />
          <span>
            {selectedRangeGdd && (
              <span className="font-bold">
                {" "}
                : {selectedRangeGdd.toFixed(2)}
              </span>
            )}
          </span>
        </div>
      </div>
      {averageDifferenceForRange !== null && (
        <div className="flex items-center sm:items-start justify-between mb-3 sm:flex-col">
          <span>
            Growth in average size in selected range :{" "}
            <span className="font-bold">
              {averageDifferenceForRange.toFixed(2)} {isImperial ? "in" : "cm"}{" "}
            </span>
          </span>
        </div>
      )}
      <div className="flex items-center sm:items-start justify-between mb-3 sm:flex-col">
        <div>
          <span>Forecast GDD for next &nbsp;</span>
          <InputNumber
            className="w-16"
            style={{ "text-align-last": "center" }}
            min={1}
            max={14}
            value={forecastDays}
            onChange={handleGDDForecastChange}
          />
          <span> &nbsp; days : </span>
          <span>
            {forecastGdd && (
              <span className="font-bold">{forecastGdd.toFixed(2)}</span>
            )}
          </span>
        </div>
      </div>
    </div>
  );
};

export { Forecast };
